'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import CustomerCardPanel, { mapPipelineRow, PIPELINE_COLUMNS } from '@/components/common/CustomerCardPanel';
import type { CustomerCard } from '@/components/common/CustomerCardPanel';
import AddCustomerModal from '../../customer-dashboard/components/AddCustomerModal';

const NAVY = '#1C2E5E';
const CYAN = '#E89A3C';

const stageMeta = (stage: string) => PIPELINE_COLUMNS.find(c => c.id === stage) || { title: stage, color: '#94A3B8' };

// Customer grid card — clicking opens the SAME full page as the sales board.
function CustomerGridCard({ card, onOpen }: { card: CustomerCard; onOpen: () => void }) {
  const initials = `${(card.customerFirstName || '').charAt(0)}${(card.customerLastName || '').charAt(0)}`.toUpperCase() || '??';
  const location = [card.city, card.state].filter(Boolean).join(', ');
  const sm = stageMeta(card.stage);
  return (
    <button onClick={onOpen} className="text-left bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${CYAN} 0%, ${NAVY} 100%)` }}>{initials}</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm font-bold leading-tight truncate" style={{ color: NAVY }}>{card.customerFirstName} {card.customerLastName}</h3>
          <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: sm.color }}>{sm.title}</span>
        </div>
      </div>
      <div className="space-y-1">
        {card.email && <p className="flex items-center gap-1.5 text-[11px] text-gray-500 truncate"><Icon name="EnvelopeIcon" size={11} className="text-gray-400 flex-shrink-0" /><span className="truncate">{card.email}</span></p>}
        {card.phone && <p className="flex items-center gap-1.5 text-[11px] text-gray-500"><Icon name="PhoneIcon" size={11} className="text-gray-400 flex-shrink-0" />{card.phone}</p>}
        {location && <p className="flex items-center gap-1.5 text-[11px] text-gray-500 truncate"><Icon name="MapPinIcon" size={11} className="text-gray-400 flex-shrink-0" /><span className="truncate">{location}</span></p>}
      </div>
    </button>
  );
}

export default function CustomersInteractive() {
  const supabase = useMemo(() => createClient(), []);
  const [cards, setCards] = useState<CustomerCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CustomerCard | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Same source of truth as the sales board.
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data, error } = await supabase.from('customer_pipeline').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setCards((data || []).filter((r: any) => !r.is_archived).map(mapPipelineRow));
    } catch (e: any) {
      setError(e?.message || 'Failed to load customers');
      setCards([]);
    } finally { setLoading(false); }
  }, [supabase]);

  useEffect(() => {
    load();
    const ch = supabase.channel('customers_pipeline')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customer_pipeline' }, () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => load(), 500);
      })
      .subscribe();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); supabase.removeChannel(ch); };
  }, [load, supabase]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return cards.filter(c => {
      const matchesSearch = !q || `${c.customerFirstName || ''} ${c.customerLastName || ''} ${c.email || ''} ${c.phone || ''} ${c.address || ''} ${c.city || ''} ${c.state || ''}`.toLowerCase().includes(q);
      const matchesStage = stageFilter === 'all' || c.stage === stageFilter;
      return matchesSearch && matchesStage;
    });
  }, [cards, search, stageFilter]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-xl font-bold" style={{ color: NAVY }}>Customers</h1>
            <p className="font-caption text-xs text-gray-500 mt-0.5">{loading ? 'Loading…' : `${filtered.length} of ${cards.length} customers`}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 font-body text-xs text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
              <Icon name="ArrowPathIcon" size={13} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-xs font-medium text-white transition-colors" style={{ backgroundColor: CYAN }}>
              <Icon name="PlusIcon" size={13} />
              New Customer
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, phone, address…" className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white" suppressHydrationWarning />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"><Icon name="XMarkIcon" size={14} /></button>}
          </div>
          <div className="relative">
            <Icon name="FunnelIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="pl-8 pr-8 py-2 rounded-lg border border-gray-200 font-body text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white appearance-none cursor-pointer" suppressHydrationWarning>
              <option value="all">All Stages</option>
              {PIPELINE_COLUMNS.map(col => <option key={col.id} value={col.id}>{col.title}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /><span className="font-body text-sm text-gray-500">Loading customers…</span></div></div>
        ) : error ? (
          <div className="flex items-center justify-center py-20"><div className="text-center"><Icon name="ExclamationTriangleIcon" size={40} className="text-red-400 mx-auto mb-3" /><p className="font-body text-sm text-red-600">{error}</p><button onClick={load} className="mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: NAVY }}>Retry</button></div></div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-20"><div className="text-center"><Icon name="UserGroupIcon" size={40} className="text-gray-300 mx-auto mb-3" /><p className="font-body text-sm text-gray-500">{search || stageFilter !== 'all' ? 'No customers match your search.' : 'No customers yet.'}</p>{(search || stageFilter !== 'all') && <button onClick={() => { setSearch(''); setStageFilter('all'); }} className="mt-3 font-body text-sm text-blue-600 hover:underline">Clear filters</button>}</div></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(c => <CustomerGridCard key={c.id} card={c} onOpen={() => setSelectedCard(c)} />)}
          </div>
        )}
      </div>

      {/* Same rich modal as the sales board — writes to customer_pipeline, defaults New Lead, shows on the board */}
      {showAddModal && <AddCustomerModal onClose={() => setShowAddModal(false)} onCreated={load} />}

      {/* Same full customer card page the board opens */}
      {selectedCard && <CustomerCardPanel card={selectedCard} onClose={() => setSelectedCard(null)} />}
    </div>
  );
}
